import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../hooks/ThemeContext';
import { CartContext } from '../hooks/CartContext';
import SectionTitle from '../components/common/SectionTitle';
import LoadingSpinner from '../components/globals/LoadingSpinner';
import { formatCurrency } from '../utils/formatters';
import { ShoppingCart, ShieldCheck, ArrowRight } from 'lucide-react';

const Cart = () => {
  const { theme } = useContext(ThemeContext);
  const { cart, loading, error, updateQuantity, removeFromCart, clearCart } = useContext(CartContext);
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  if (loading) {
    return (
      <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
        <div className="flex justify-center items-center py-20">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  const handleQuantityChange = async (itemId, newQuantity) => {
    if (newQuantity < 1) {
      await removeFromCart(itemId);
    } else {
      await updateQuantity(itemId, newQuantity);
    }
  };

  const handleRemoveItem = async (itemId) => {
    await removeFromCart(itemId);
  };

  const handleClearCart = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir vider votre panier ?')) {
      await clearCart();
    }
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  const itemCount = cart.items_count || cart.items?.length || 0;

  return (
    <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
      <div className={`${isDark ? 'bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 border-b border-white/10' : 'bg-gradient-to-br from-rose-50 via-purple-50 to-blue-50 border-b border-gray-200'}`}>
        <div className="container mx-auto px-4 py-12 lg:py-16 max-w-5xl">
          <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr] items-start">
            <div className="space-y-5">
              <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm shadow-xl ${isDark ? 'border-white/10 bg-white/5 text-gray-200' : 'border-gray-200 bg-white text-gray-700'}`}>
                <ShoppingCart className="w-4 h-4 text-rose-400" />
                Panier clair et premium
              </div>

              <SectionTitle 
                title="Votre panier"
                subtitle="Gérez vos articles avant de procéder au paiement"
                align="left"
              />

              <p className={`max-w-2xl text-base leading-8 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Une vue plus propre pour vérifier vos créations, ajuster les quantités et passer au paiement sans friction.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
              {[
                { label: 'Articles', value: itemCount, icon: <ShoppingCart className="w-5 h-5 text-rose-400" /> },
                { label: 'Livraison', value: 'Offerte', icon: <ShieldCheck className="w-5 h-5 text-rose-400" /> },
                { label: 'Total', value: formatCurrency(cart.total || 0), icon: <ArrowRight className="w-5 h-5 text-rose-400" /> },
              ].map((item) => (
                <div key={item.label} className={`rounded-3xl border p-5 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'}`}>
                  <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 shadow-xl">
                    {item.icon}
                  </div>
                  <p className="text-xl font-bold mb-1">{item.value}</p>
                  <p className={`${isDark ? 'text-sm text-gray-300' : 'text-sm text-gray-600'}`}>{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-5xl">

        {/* Message d'erreur */}
        {error && (
          <div className={`p-4 rounded-3xl mb-6 shadow-xl ${isDark ? 'bg-red-500/20 text-red-300 border border-red-500/30' : 'bg-red-50 text-red-800 border border-red-200'}`}>
            {error}
          </div>
        )}

        {/* Panier vide */}
        {!loading && (!cart.items || cart.items.length === 0) ? (
          <div className={`text-center py-14 px-6 ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'} rounded-3xl shadow-xl`}>
            <div className="text-5xl mb-4">🛒</div>
            <h3 className="text-2xl font-semibold mb-2">Panier vide</h3>
            <p className={`mb-6 leading-8 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
              Vous n'avez pas encore ajouté de produits à votre panier.
            </p>
            <button
              onClick={() => navigate('/shop')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-rose-600 text-white rounded-2xl font-semibold hover:bg-rose-700 transition-colors shadow-xl"
            >
              Découvrir nos créations
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Liste des articles */}
            <div className="lg:col-span-2">
              <div className={`rounded-3xl overflow-hidden shadow-xl ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'}`}>
                
                {/* En-tête du tableau */}
                <div className={`grid grid-cols-4 gap-4 p-6 border-b ${isDark ? 'border-white/10 bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="col-span-2 font-semibold">Produit</div>
                  <div className="font-semibold text-center">Quantité</div>
                  <div className="font-semibold text-right">Prix</div>
                </div>

                {/* Articles */}
                <div className="divide-y" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#e5e7eb' }}>
                  {cart.items.map((item) => (
                    <div key={item.id} className={`grid grid-cols-4 gap-4 p-6 items-center transition-colors ${isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-gray-50'}`}>
                      
                      {/* Produit */}
                      <div className="col-span-2">
                        <h4 className="font-semibold mb-1">{item.product?.name || 'Produit'}</h4>
                        <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                          {item.product?.id_product && `ID: ${item.product.id_product}`}
                        </p>
                      </div>

                      {/* Quantité */}
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          className={`px-3 py-2 rounded-2xl text-sm font-medium transition-colors shadow-xl ${
                            isDark
                              ? 'bg-white/10 text-white hover:bg-rose-600'
                              : 'bg-gray-100 text-gray-800 hover:bg-rose-600 hover:text-white'
                          }`}
                        >
                          −
                        </button>
                        <span className="w-8 text-center font-semibold">{item.quantity}</span>
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                          className={`px-3 py-2 rounded-2xl text-sm font-medium transition-colors shadow-xl ${
                            isDark
                              ? 'bg-white/10 text-white hover:bg-rose-600'
                              : 'bg-gray-100 text-gray-800 hover:bg-rose-600 hover:text-white'
                          }`}
                        >
                          +
                        </button>
                      </div>

                      {/* Prix et suppression */}
                      <div className="text-right">
                        <p className="font-semibold mb-2">{formatCurrency(item.price)}</p>
                        <button
                          onClick={() => handleRemoveItem(item.id)}
                          className="text-sm text-rose-500 hover:text-rose-600 font-medium transition-colors"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div className={`p-6 border-t text-right ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                  <button
                    onClick={handleClearCart}
                    className={`text-sm font-medium transition-colors ${
                      isDark
                        ? 'text-white/60 hover:text-rose-400'
                        : 'text-gray-600 hover:text-rose-600'
                    }`}
                  >
                    Vider le panier
                  </button>
                </div>
              </div>
            </div>

            {/* Résumé et checkout */}
            <div className="lg:col-span-1">
              <div className={`rounded-3xl p-6 sticky top-24 shadow-xl ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'}`}>
                
                <h3 className="text-lg font-semibold mb-6">Résumé de la commande</h3>

                {/* Détails */}
                <div className={`space-y-4 pb-6 border-b ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                  <div className="flex justify-between text-sm">
                    <span className={isDark ? 'text-white/70' : 'text-gray-600'}>Articles ({itemCount})</span>
                    <span className="font-medium">{formatCurrency(cart.total)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className={isDark ? 'text-white/70' : 'text-gray-600'}>Livraison</span>
                    <span className="font-medium">Gratuite</span>
                  </div>
                </div>

                {/* Total */}
                <div className="flex justify-between text-lg font-bold my-6">
                  <span>Total</span>
                  <span className="text-rose-600">{formatCurrency(cart.total)}</span>
                </div>

                {/* Boutons */}
                <div className="space-y-3">
                  <button
                    onClick={handleCheckout}
                    className="w-full px-4 py-3 bg-rose-600 text-white font-semibold rounded-2xl hover:bg-rose-700 transition-colors shadow-xl"
                  >
                    Procéder au paiement
                  </button>
                  <button
                    onClick={() => navigate('/shop')}
                    className={`w-full px-4 py-3 border font-medium rounded-2xl transition-colors shadow-xl ${
                      isDark
                        ? 'border-white/20 text-white bg-white/5 hover:bg-white/10'
                        : 'border-gray-300 text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    Continuer vos achats
                  </button>
                </div>

                {/* Sécurité */}
                <div className={`mt-6 pt-6 border-t text-xs text-center ${isDark ? 'border-white/10 text-white/60' : 'border-gray-100 text-gray-600'}`}>
                  <p>🔒 Paiement sécurisé par Stripe</p>
                </div>

              </div>
            </div>

          </div>
        )}

      </div>
    </div>
  );
};

export default Cart;
